/**
 * What a CSV column holds, to help the person place it: an inferred type and a
 * few sample values. Only a hint — every cell is still saved as text unless it
 * is JSON and "Parse JSON values" is on.
 */

import type { ParsedCsv } from "@/lib/csv";
import { parseCell } from "../csv-mapping";

export type ColumnType = "string" | "number" | "boolean" | "json";

export interface ColumnProfile {
  type: ColumnType;
  /** Up to three non-empty values, from the top of the file. */
  samples: string[];
  filled: number;
}

function typeOf(cell: string): ColumnType {
  if (/^(true|false)$/i.test(cell)) return "boolean";
  if (cell !== "" && Number.isFinite(Number(cell))) return "number";
  return typeof parseCell(cell) === "string" ? "string" : "json";
}

/** The one type every non-empty cell shares, else string. */
export function inferType(cells: string[]): ColumnType {
  const types = new Set(cells.map(typeOf));
  return types.size === 1 ? [...types][0]! : "string";
}

export function profileColumns(csv: ParsedCsv): ColumnProfile[] {
  return csv.header.map((_, i) => {
    const cells = csv.rows.map((r) => (r[i] ?? "").trim()).filter(Boolean);
    return { type: inferType(cells), samples: cells.slice(0, 3), filled: cells.length };
  });
}
